const normalizeText = (value?: string | null): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\w\s-]/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const normalizeRoute = (route?: string | null): string =>
  String(route || '').trim().replace(/\/+$/, '').toLowerCase()

const HIDDEN_ROUTE_SET = new Set<string>([
  '/operations/inport-consumption',
  '/procurement/overview',
  '/procurement/company-po-info',
  '/procurement/audit-trail',
  '/procurement/user-management',
])

const HIDDEN_PLACEMENT_SET = new Set<string>([
  'operations::in port consumption report::',
  'technical::procurement::overview',
  'technical::procurement::company po info',
  'technical::procurement::audit trail',
  'technical::procurement::user management',
  'procurement::overview::',
  'procurement::company po info::',
  'procurement::audit trail::',
  'procurement::user management::',
])

const HIDDEN_PERMISSION_CODE_SET = new Set<string>([
  'READ_IN_PORT_CONSUMPTION_REPORT',
  'UPDATE_IN_PORT_CONSUMPTION_REPORT',
  'READ_PROCUREMENT_OVERVIEW',
  'PROCUREMENT_LOG_VIEW',
  'USER_PERMISSION_VIEW',
  'USER_PERMISSION_MANAGE',
])

export const PERMANENTLY_HIDDEN_MENU_KEYS = Object.freeze({
  routes: Array.from(HIDDEN_ROUTE_SET.values()),
  placements: Array.from(HIDDEN_PLACEMENT_SET.values()),
  permissionCodes: Array.from(HIDDEN_PERMISSION_CODE_SET.values()),
})

const placementKey = (
  moduleLabel?: string | null,
  menuLabel?: string | null,
  subMenuLabel?: string | null
): string =>
  `${normalizeText(moduleLabel)}::${normalizeText(menuLabel)}::${normalizeText(subMenuLabel)}`

export const isPermanentlyHiddenRoute = (route?: string | null): boolean =>
  HIDDEN_ROUTE_SET.has(normalizeRoute(route))

export const isPermanentlyHiddenPlacement = (
  moduleLabel?: string | null,
  menuLabel?: string | null,
  subMenuLabel?: string | null
): boolean => HIDDEN_PLACEMENT_SET.has(placementKey(moduleLabel, menuLabel, subMenuLabel))

export const isPermanentlyHiddenPermissionCode = (permissionCode?: string | null): boolean =>
  HIDDEN_PERMISSION_CODE_SET.has(String(permissionCode || '').trim().toUpperCase())

interface MenuLikeNode {
  name?: string
  title?: string
  route?: string
  submenu?: MenuLikeNode[]
}

export function filterPermanentlyHiddenMenuNodes<T extends MenuLikeNode>(nodes: T[]): T[] {
  return (nodes || [])
    .map((node) => {
      if (isPermanentlyHiddenRoute(node.route)) {
        return null
      }

      const children = Array.isArray(node.submenu)
        ? filterPermanentlyHiddenMenuNodes(node.submenu)
        : undefined

      if (children && Array.isArray(node.submenu)) {
        return {
          ...node,
          submenu: children,
        }
      }

      return node
    })
    .filter(Boolean) as T[]
}

interface StructureLikeNode {
  label?: string
  route?: string
  children?: StructureLikeNode[]
}

export function filterPermanentlyHiddenStructureNodes<T extends StructureLikeNode>(
  moduleLabel: string,
  nodes: T[],
  parentLabel?: string | null
): T[] {
  return (nodes || [])
    .map((node) => {
      const isHiddenPlacement = isPermanentlyHiddenPlacement(
        moduleLabel,
        parentLabel || node.label,
        parentLabel ? node.label : null
      )
      if (isHiddenPlacement || isPermanentlyHiddenRoute(node.route)) {
        return null
      }

      const children = Array.isArray(node.children)
        ? filterPermanentlyHiddenStructureNodes(moduleLabel, node.children, node.label)
        : undefined

      if (children && Array.isArray(node.children)) {
        return {
          ...node,
          children,
        }
      }

      return node
    })
    .filter(Boolean) as T[]
}
